/** The sandbox banners under an operator page's header, on every tab (SBX D3 "Warning", D4 §3–4, D6):
 *
 *  - on a sandbox: suspended (and by whom: an ops suspension is locked), inbound paused (the circuit
 *    breaker tripped), lane paused (a booking quota reached), and the production-key banner — its
 *    vendor key matches a production key, or credentials were disabled for reaching production;
 *  - on a production operator with a sandbox: "Your sandbox acmesbx uses this … key".
 *
 *  The production-key sentences are the backend's own (`banner[].text`), so the console, the setup
 *  wizard's credential item and the Developer section say the same thing. Each alert links to the
 *  Sandbox tab, where the state is explained and changed — except on that tab itself. */
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { SandboxState } from '@/api/sandbox';
import type { TourOperatorDetail } from '@/api/tourOperators';

import { breakerReasonText, suspendedByText, throttleText } from './sandboxWords';
import { useSandboxState } from './useSandboxState';
import { When } from './When';

export function SandboxBanners({
  to,
  onSandboxTab,
}: {
  to: TourOperatorDetail;
  onSandboxTab: boolean;
}) {
  const { data } = useSandboxState(to);
  if (!data) return null;
  const alerts = sandboxAlerts(data);
  if (alerts.length === 0) return null;
  const tabLink = onSandboxTab ? undefined : (
    <Button
      component={RouterLink}
      to={`/tour-operators/${to.id}/sandbox`}
      color="inherit"
      size="small"
    >
      Sandbox tab
    </Button>
  );
  return (
    <Stack spacing={1} sx={{ mb: 2 }} data-sandbox-banners>
      {alerts.map((a) => (
        <Alert key={a.key} severity={a.severity} action={tabLink}>
          {a.title && <AlertTitle sx={{ mb: 0.25 }}>{a.title}</AlertTitle>}
          {a.body}
        </Alert>
      ))}
    </Stack>
  );
}

interface BannerAlert {
  key: string;
  severity: 'error' | 'warning' | 'info';
  title?: string;
  body: ReactNode;
}

export function sandboxAlerts(s: SandboxState): BannerAlert[] {
  const out: BannerAlert[] = [];
  const susp = s.suspension;
  if (s.environment === 'sandbox' && susp?.suspended) {
    out.push({
      key: 'suspended',
      severity: 'warning',
      title: susp.locked ? `${suspendedByText(susp)} — locked` : suspendedByText(susp),
      body: (
        <>
          Every operation of this sandbox is stopped: API writes, tasks, sends and crons. Reads stay
          allowed.
          {susp.at && (
            <>
              {' '}
              Since <When iso={susp.at} />.
            </>
          )}
          {susp.reason && ` Reason: ${susp.reason}`}
          {susp.locked && ' Its own admins cannot resume it; ops can.'}
        </>
      ),
    });
  }
  const breaker = s.breaker;
  if (s.environment === 'sandbox' && breaker?.tripped) {
    out.push({
      key: 'breaker',
      severity: 'warning',
      title: 'Inbound paused — the circuit breaker tripped',
      body: (
        <>
          {breakerReasonText(breaker)}
          {breaker.since && (
            <>
              , <When iso={breaker.since} />
            </>
          )}
          . bproxy holds the sandbox’s new requests and delivers nothing until the breaker is reset.
        </>
      ),
    });
  }
  if (s.environment === 'sandbox' && s.throttle) {
    out.push({
      key: 'throttle',
      severity: 'warning',
      title: 'Lane paused — a booking quota is reached',
      body: `${throttleText(s.throttle)}. New requests are answered 429 quota_exceeded; production is not affected.`,
    });
  }
  (s.banner ?? []).forEach((b, i) => {
    out.push({
      key: `banner-${i}`,
      severity: b.kind === 'disabled' ? 'error' : b.kind === 'used_by_sandbox' ? 'info' : 'warning',
      body: b.text,
    });
  });
  return out;
}
